import React from "react";
import { Box, Card, CardContent, Chip, Divider, Typography } from "@mui/material";
import ColorService from "../../services/ColorService";
import UtilService from "../../services/UtilService";

const formatType = (type) => {
    if (!type) return type;
    // Convert HIDDEN_MESSAGE to "Hidden Message", WORD_SEARCH to "Word Search", etc.
    return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const PuzzleCard = ({ puzzle, onClick }) => {
    return (
        <Card
            sx={{
                height: '100%',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                }
            }}
            onClick={onClick}
        >
            <CardContent>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.3,
                        minHeight: '2.6em'
                    }}
                >
                    {puzzle.title || 'Untitled Puzzle'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {puzzle.type && (
                        <Chip label={formatType(puzzle.type)} size="small" color="primary" variant="outlined" />
                    )}
                    {puzzle.state && (
                        <Chip
                            label={puzzle.state}
                            size="small"
                            color={
                                puzzle.state === "DISCARDED" ? "error" :
                                    puzzle.state === "READY_TO_REVIEW" ? "success" :
                                        "secondary"
                            }
                            variant={
                                puzzle.state === "DISCARDED" || puzzle.state === "READY_TO_REVIEW" ? "filled" : "outlined"
                            }
                        />
                    )}
                    {puzzle.difficultyLevel && (
                        <Chip
                            label={puzzle.difficultyLevel?.toLowerCase()}
                            size="small"
                            sx={{
                                backgroundColor: ColorService.getDifficultyColor(puzzle.difficultyLevel),
                                color: "white",
                                textTransform: "capitalize"
                            }}
                        />
                    )}
                </Box>
                {puzzle.genre && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Genre: {puzzle.genre.name || 'N/A'}
                    </Typography>
                )}
                {puzzle.tags && Array.isArray(puzzle.tags) && puzzle.tags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                        {puzzle.tags
                            .filter((tag) => {
                                const label = typeof tag === "string" ? tag : (tag?.name ?? tag?.label ?? String(tag));
                                return String(label).toLowerCase() !== "bedrock";
                            })
                            .map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={typeof tag === "string" ? tag : (tag?.name ?? tag?.label ?? String(tag))}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: "0.75rem", height: 22 }}
                                />
                            ))}
                    </Box>
                )}
                <Divider sx={{ my: 1 }} />
                {(puzzle.creatorId || puzzle.creator || puzzle.cloudUser) && (() => {
                    const getCreatorDisplay = () => {
                        const creator = puzzle.creator || puzzle.cloudUser;
                        if (creator) {
                            const firstName = creator.firstName || '';
                            const lastName = creator.lastName || '';
                            const email = creator.email || '';
                            const nameParts = [firstName, lastName].filter(Boolean);
                            if (nameParts.length > 0 && email) {
                                return `${nameParts.join(' ')} (${email})`;
                            } else if (nameParts.length > 0) {
                                return nameParts.join(' ');
                            } else if (email) {
                                return email;
                            }
                        }
                        return puzzle.creatorName || puzzle.creatorId || 'Unknown';
                    };
                    return (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Creator: {getCreatorDisplay()}
                        </Typography>
                    );
                })()}
                {puzzle.updatedAt && (() => {
                    const durationMs = Date.now() - new Date(puzzle.updatedAt).getTime();
                    const humanized = UtilService.formatHumanize(durationMs);
                    return humanized ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                            Updated: {humanized} ago
                        </Typography>
                    ) : null;
                })()}
                {puzzle.createdAt && (() => {
                    const durationMs = Date.now() - new Date(puzzle.createdAt).getTime();
                    const humanized = UtilService.formatHumanize(durationMs);
                    return humanized ? (
                        <Typography variant="caption" color="text.secondary">
                            Created: {humanized} ago
                        </Typography>
                    ) : null;
                })()}
            </CardContent>
        </Card>
    );
};

export default PuzzleCard;

